import type { 
  MerchantProduct as ApiMerchantProduct
} from '../types'
import type { ProductData } from '../components/ProductCreationWizard/ProductCreationWizard'
import { getBestImage, resolveVariantImage } from './imageHelpers'
import {
  calculateTotalStock,
  getStockStatus,
  getCurrentPrice,
  getPrimaryCategory,
  calculatePriceRange,
  transformVariantCombinationsForUI
} from './uiTransformHelpers'
import {
  transformImagesToPhotos,
  transformVariantCombinations,
  transformVariantOptions,
  transformShippingCompanies,
  transformCertifications,
  transformVideos,
  transformCategoryAttributes,
  buildProductObject
} from './wizardTransformHelpers'

// UI-specific interface for the current components
export interface UIMerchantProduct {
  id: string
  sku: string
  name: string
  brand: string
  category: string
  image?: string
  price: {
    current: number
    currency: string
    isEditable: boolean
  }
  stock: {
    quantity: number
    status: 'in-stock' | 'low-stock' | 'out-of-stock' | 'warning'
    isEditable: boolean
  }
  sales: {
    quantity30d: number
    revenue30d: number
  }
  variants?: {
    count: number
    priceRange?: {
      min: number
      max: number
    }
    combinations: {
      id: string
      sku: string
      isActive: boolean
      stock: number
      price: number
      weight?: number
      tierVariation: Record<string, string>[]
      image?: string
    }[]
  }
  actions: ('edit')[]
  createdAt: string
  updatedAt: string
}

/**
 * Transform service-converted MerchantProduct to UI format
 * This function adapts the service-transformed data structure to the current UI's expected format
 * Note: Input is already transformed from snake_case API to camelCase by the service layer
 */
export function transformApiToUIProduct(merchantProduct: ApiMerchantProduct): UIMerchantProduct {
  // Safety check for null/undefined product
  if (!merchantProduct) {
    console.error('❌ Merchant Product is null or undefined')
    throw new Error('Cannot transform null or undefined merchant product')
  }
  
  // Use helper functions for calculations
  const primaryCategory = getPrimaryCategory(merchantProduct.category)
  const totalStock = calculateTotalStock(merchantProduct.variantCombinations)
  const stockStatus = getStockStatus(totalStock)
  const currentPrice = getCurrentPrice(merchantProduct)

  // Create variant information if product has variants
  const variants = merchantProduct.isProductHasVariants && merchantProduct.variantCombinations?.length > 0 ? {
    count: merchantProduct.variantCombinations.length,
    priceRange: calculatePriceRange(merchantProduct.variantCombinations),
    combinations: transformVariantCombinationsForUI(
      merchantProduct.variantCombinations,
      merchantProduct.variantOptions,
      resolveVariantImage
    )
  } : undefined

  return {
    // Clean UI-compatible structure
    id: merchantProduct.id || '',
    sku: merchantProduct.sku || '',
    name: merchantProduct.name || 'Unnamed Product',
    brand: merchantProduct.brandName || 'Unknown Brand',
    category: primaryCategory,
    image: getBestImage(merchantProduct), // Use smart image selection logic
    price: {
      current: currentPrice,
      currency: 'IDR', // Default currency, could be derived from API
      isEditable: true
    },
    stock: {
      quantity: totalStock,
      status: stockStatus as 'in-stock' | 'low-stock' | 'out-of-stock' | 'warning',
      isEditable: true
    },
    sales: {
      quantity30d: merchantProduct.salesMetrics?.salesQty30d || 0,
      revenue30d: merchantProduct.salesMetrics?.salesRevenue30d || 0
    },
    variants,
    actions: ['edit' as const],
    createdAt: merchantProduct.createdAt,
    updatedAt: merchantProduct.updatedAt
  }
}

/**
 * Transform UI filter format to API filter format
 */
export function transformUIToApiFilters(uiFilters: {
  search: string
  stockRange: { min: number | null; max: number | null }
  priceRange: { min: number | null; max: number | null }
  salesRange: { min: number | null; max: number | null }
}) {
  return {
    search: uiFilters.search,
    brandName: '',
    category: '',
    hasVariants: undefined,
    hasBundles: undefined,
    isActive: true,
    priceRange: uiFilters.priceRange,
    salesRange: uiFilters.salesRange,
    sortBy: 'name' as const,
    sortOrder: 'desc' as const
  }
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency: string = 'IDR'): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(price)
}

/**
 * Format stock status for display
 */
export function getStockStatusInfo(stock: number) {
  if (stock === 0) {
    return { status: 'out-of-stock', label: 'Out of Stock', className: 'text-red-600 font-bold' }
  }
  if (stock <= 10) {
    return { status: 'warning', label: 'Low Stock', className: 'text-amber-600 font-bold' }
  }
  return { status: 'in-stock', label: 'In Stock', className: 'text-gray-800' }
}

/**
 * Transform wizard ProductData to API payload format
 */
export function transformWizardDataToApiPayload(productData: ProductData, isEditMode: boolean = false) {
  const { basicInfo, classification, salesInfo, shipping } = productData

  // Transform attributes using helper
  const transformedAttributes = transformCategoryAttributes(classification.categoryAttributes)

  // Build the main product object using helper
  const product = buildProductObject(productData, isEditMode)
  
  // Add attributes if transformed
  if (transformedAttributes.length > 0) {
    product.attributes = transformedAttributes
  }

  // Transform data using helpers
  const photos = transformImagesToPhotos(basicInfo.productImages)

  const variant_combinations = transformVariantCombinations(salesInfo, shipping)

  const shippers = transformShippingCompanies(shipping.shippingCompanies)
  const variant_options = transformVariantOptions(salesInfo)
  const certifications = transformCertifications(
    classification.licenseFileId,
    classification.licenseFileUrl,
    classification.licenseUploadStatus
  )
  const videos = transformVideos(basicInfo.productVideo)

  const payload: any = {
    product
  }

  // In edit mode, only include subcollections if they have data or are explicitly being cleared
  // In create mode, include all relevant subcollections
  
  if (photos && photos.length > 0) {
    payload.photos = photos
  } else if (!isEditMode) {
    payload.photos = [] // Always include empty array for create mode
  }

  if (variant_combinations && variant_combinations.length > 0) {
    payload.variant_combinations = variant_combinations
  } else if (!isEditMode) {
    payload.variant_combinations = [] // Always include empty array for create mode
  }

  if (shippers && shippers.length > 0) {
    payload.shippers = shippers
  } else if (!isEditMode) {
    payload.shippers = [] // Always include empty array for create mode
  }

  if (variant_options && variant_options.length > 0) {
    payload.variant_options = variant_options
  } else if (!isEditMode) {
    payload.variant_options = [] // Always include empty array for create mode
  }

  if (certifications && certifications.length > 0) {
    payload.certifications = certifications
  } else if (!isEditMode) {
    payload.certifications = [] // Always include empty array for create mode
  }

  if (videos && videos.length > 0) {
    payload.videos = videos
  } else if (!isEditMode) {
    payload.videos = [] // Always include empty array for create mode
  }

  // Bundles - only add if not a bundle product
  if (!isEditMode && product.is_bundle === false) {
    payload.bundles = [] // Only add empty array for create mode
  }

  return payload
}

/**
 * Transform API product data to wizard format for editing
 */
export function transformApiProductToWizardData(apiProduct: ApiMerchantProduct): Partial<ProductData> {
  console.log('🔄 Transforming API product to wizard format for editing:', apiProduct.id, apiProduct.name)
  
  // Transform variant options from API format to wizard format
  const transformedVariantOptions = apiProduct.variantOptions?.map((option, index) => ({
    id: option.id,
    name: option.name,
    index: index,
    values: option.optionList?.map(opt => ({
      id: `${option.id}_${opt.option}`,
      name: opt.option,
      image: opt.image || null
    })) || []
  })) || []

  // Transform variant combinations with proper option mapping
  const transformedVariantCombinations = apiProduct.variantCombinations?.map((variant, index) => {
    // Build proper options and option values arrays from tier_variation
    const options: string[] = []
    const optionValues: string[] = []
    
    if (variant.tierVariation && Array.isArray(variant.tierVariation)) {
      variant.tierVariation.forEach(tier => {
        const [key, value] = Object.entries(tier)[0] || []
        if (key && value) {
          options.push(key)
          optionValues.push(value as string)
        }
      })
    }

    return {
      id: variant.id || `variant_${index}`,
      name: options.length > 0 ? optionValues.join(' - ') : `Variant ${index + 1}`,
      options,
      optionValues,
      tierIndex: variant.tierIndex || [],
      price: variant.price || 0,
      cost: 0, // TODO: Extract cost price if available
      stock: variant.stock || 0,
      sku: variant.sku || '',
      warehouseStock: { main: variant.stock || 0 }, // TODO: Handle multiple warehouses from API
      isActive: variant.isActive ?? true,
      isMainVariant: index === 0 || variant.tierIndex?.[0] === 0 // First variant or first tier index
    }
  }) || []

  // Transform category attributes from API to wizard format
  const categoryAttributes: Record<string, string> = {}
  if (apiProduct.attributes && Array.isArray(apiProduct.attributes)) {
    apiProduct.attributes.forEach(attr => {
      categoryAttributes[attr.name] = attr.value
    })
  }

  // Transform shipping companies - since API doesn't return shipping companies directly,
  // we'll create a default structure based on shippers data
  const transformedShippingCompanies = apiProduct.shippers?.map(shipper => {
    // Map common shipping company names to icons and colors
    const shipperLower = shipper.name.toLowerCase()
    let icon = 'local_shipping'
    let color = 'gray-600'
    
    if (shipperLower.includes('jne')) {
      icon = 'local_shipping'
      color = 'red-600'
    } else if (shipperLower.includes('jnt') || shipperLower.includes('j&t')) {
      icon = 'local_shipping'
      color = 'red-500'
    } else if (shipperLower.includes('sicepat')) {
      icon = 'speed'
      color = 'orange-600'
    } else if (shipperLower.includes('anteraja')) {
      icon = 'local_shipping'
      color = 'purple-600'
    } else if (shipperLower.includes('gosend') || shipperLower.includes('grab')) {
      icon = 'two_wheeler'
      color = 'green-600'
    }
    
    return {
      id: shipper.id,
      name: shipper.name,
      icon,
      color,
      enabled: shipper.isActive,
      services: [
        {
          id: `${shipper.id}_standard`,
          name: 'Standard Delivery',
          description: 'Standard delivery service',
          enabled: shipper.isActive
        }
      ]
    }
  }) || []

  // Transform pre-order settings
  const preOrderSettings = apiProduct.preOrder ? {
    enabled: apiProduct.preOrder.isEnabled,
    type: apiProduct.preOrder.type || 'PRE_ORDER' as const,
    processingDays: apiProduct.preOrder.shipInDays || 7
  } : {
    enabled: false,
    type: 'PRE_ORDER' as const,
    processingDays: 7
  }

  const wizardData: Partial<ProductData> = {
    basicInfo: {
      productSku: apiProduct.sku || '',
      productName: apiProduct.name || '',
      productDescription: apiProduct.description || '',
      productImages: apiProduct.photos?.map((photo, index) => ({
        id: photo.hash || index.toString(),
        url: photo.url,
        file: null,
        isMain: photo.isPrimary || index === 0,
        alt: apiProduct.name || 'Product Image',
        size: 0,
        uploaded: true,
        order: index
      })) || [],
      videoUrl: '' // API doesn't store video URL, leave empty for user to add
    },
    classification: {
      brandId: apiProduct.companyId, // Use company ID as brand reference
      brandName: apiProduct.brandName || '',
      categoryPath: apiProduct.category?.map(cat => ({
        id: cat.id,
        name: cat.id, // API returns category ID, ideally we'd fetch category names
        hasChildren: cat.hasChildren || false,
        isActive: true
      })) || [],
      categoryAttributes,
      licenseFile: null // No license file in API response, user can re-upload if needed
    },
    salesInfo: {
      minOrder: apiProduct.minOrder || 1,
      hasVariants: apiProduct.isProductHasVariants || false,
      productPrice: !apiProduct.isProductHasVariants ? (
        apiProduct.pricingMetrics?.averageSellingPrice || 
        (transformedVariantCombinations[0]?.price || 0)
      ) : 0,
      costPrice: 0, // TODO: Extract from API if available
      salePrice: undefined, // TODO: Extract sale price if available
      currency: 'IDR', // Default, TODO: extract from API
      totalStock: !apiProduct.isProductHasVariants ? 
        apiProduct.variantCombinations?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0 : 0,
      warehouseStock: !apiProduct.isProductHasVariants ? {
        main: apiProduct.variantCombinations?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0
      } : {},
      tags: apiProduct.tags || [],
      promotionTags: apiProduct.promotionTags || [],
      variantOptions: transformedVariantOptions,
      variantCombinations: transformedVariantCombinations
    },
    shipping: {
      packageDimensions: {
        height: apiProduct.dimension?.height || 0,
        length: apiProduct.dimension?.length || 0,
        width: apiProduct.dimension?.width || 0,
        weight: apiProduct.weight || 0
      },
      isDangerous: apiProduct.isDangerousProduct || false,
      packingInstructions: apiProduct.packingInstruction || '',
      shippingCompanies: transformedShippingCompanies,
      preOrderSettings
    },
    review: {
      publishImmediately: apiProduct.isActive ?? true
    }
  }

  console.log('✅ Transformed wizard data:', {
    hasBasicInfo: !!wizardData.basicInfo,
    hasClassification: !!wizardData.classification,
    hasSalesInfo: !!wizardData.salesInfo,
    hasShipping: !!wizardData.shipping,
    hasReview: !!wizardData.review,
    variantCount: wizardData.salesInfo?.variantCombinations?.length || 0,
    imageCount: wizardData.basicInfo?.productImages?.length || 0,
    attributeCount: Object.keys(wizardData.classification?.categoryAttributes || {}).length
  })

  return wizardData
}

/**
 * Calculate analytics summaries from API data
 */
export function calculateAnalyticsSummary(products: ApiMerchantProduct[]) {
  if (products.length === 0) {
    return {
      totalProducts: 0,
      totalRevenue30d: 0,
      totalSales30d: 0,
      averagePrice: 0,
      topPerformers: []
    }
  }

  const totalRevenue30d = products.reduce((sum, p) => sum + p.salesMetrics.salesRevenue30d, 0)
  const totalSales30d = products.reduce((sum, p) => sum + p.salesMetrics.salesQty30d, 0)
  const averagePrice = products.reduce((sum, p) => sum + p.pricingMetrics.averageSellingPrice, 0) / products.length

  const topPerformers = products
    .sort((a, b) => b.salesMetrics.salesRevenue30d - a.salesMetrics.salesRevenue30d)
    .slice(0, 5)
    .map(p => ({
      id: p.id,
      name: p.name,
      revenue30d: p.salesMetrics.salesRevenue30d,
      salesQty30d: p.salesMetrics.salesQty30d
    }))

  return {
    totalProducts: products.length,
    totalRevenue30d,
    totalSales30d,
    averagePrice,
    topPerformers
  }
}